/**
 * The three generated hearing documents — the Hearing Letter (§10), the Minutes (§11) and
 * the Decision Letter (§12).
 *
 * Authority: `docs/specs/control-centre/06-hearings.md` §10, §11 and §12, whose templates
 * are reproduced here **verbatim**, plus §2 (the bound instruments every citation comes
 * from), §7 (the level ladder) and §1 (why generation exists at all).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THESE ARE PURE FUNCTIONS IN `shared`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §1 states the whole purpose of this module in one paragraph, and it is worth restating
 * because it is the reason this file is tested as hard as it is:
 *
 * > One archived "Oman AG2023" file actually contains the Chile WWC2022 minutes with a
 * > mangled header; a draft lists a Pakistani player as "No 16, South Africa"; shirt numbers
 * > conflict within a single document; "him" is used for a female player.
 *
 * Every one of those is a copy-paste defect, and a generator pre-filling from structured
 * data eliminates the class. That only holds if the generator itself is right, so it is
 * written as three total functions from data to text — no I/O, no clock, no locale
 * surprises, no database — which means every clause below can be pinned by a test that
 * reads like the specification paragraph it came from.
 *
 * It lives in `packages/shared` for the reason the lattice does: the API composes the
 * document and the page renders it, and there must be one answer to "what does this letter
 * say".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PLAIN TEXT, AND THE PAPER IS THE PAGE'S JOB
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * These return plain text, because §10 and §12 give plain-text templates and the letters
 * are read as letters. The letterhead — competition logo, the full event name, the
 * governing-body logo (§9's "Paper letterhead") — is applied by the document view when it
 * renders, not baked into the string, so the same body can be shown on screen, printed, and
 * later written into a `.doc` without three different escapings of the same words.
 *
 * §9's critical rendering rule belongs to the renderer and is repeated here because it
 * constrains what this file may return: *"issued documents must render into a read-only
 * document view, never a textarea"*. Nothing below emits markup.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOTHING HERE IS A CONSTANT THAT SHOULD BE CONFIGURATION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §2 makes versioning mandatory — *"never a hard-coded list"* — and F5 requires the TD's
 * signature block to come from Configuration §04 rather than from a constant. So the event
 * name, the instrument title, the citation string, the venue, the TD's four signature lines,
 * the minimum-penalty ladder and the three regulation citations all arrive in
 * {@link HearingDocumentConstants} and none of them has a default in this file. A tournament
 * that has not been configured cannot generate a letter, and that is the correct outcome: a
 * letter citing the wrong Code edition is worse than no letter, because it goes out.
 *
 * British English throughout. The letters themselves keep the spec's own wording, including
 * where it is terser than house style — it is the wording Alfred settled on 2026-08-05, and
 * §10's brevity rule ("must not be wordy … the Paris 2024 letter is the model") is a
 * requirement, not a preference.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * Formatting — one definition each, because a date in two spellings is a defect
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The three-letter month and weekday names the letters use, as FIXED TABLES.
 *
 * They are not taken from `Intl`, and that is a correctness decision rather than a
 * preference. Node's `en-GB` renders September as **"Sept"** — four letters — while the
 * spec's templates, and every reference letter they were distilled from, say `Tue 22 Sep`.
 * Worse, which of the two you get depends on the ICU data compiled into the runtime, so a
 * letter generated on the tournament laptop could differ from the one generated on the
 * server for no reason a Technical Delegate could ever diagnose.
 *
 * A generated legal document may not vary with the Node build it was produced on. The
 * timezone still comes from `Intl` — that is a calendar calculation, not a spelling.
 */
const MONTHS_SHORT = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/**
 * The minute mark written after a match minute — U+2032 PRIME, not an ASCII apostrophe.
 *
 * Ruled by the TD after live testing 2026-09-10: every generated document writes a match
 * minute the one way the dashboard already does (`3rd Quarter · 33′`), so a minute never
 * reads as a bare number that a reader could mistake for a shirt number or a score.
 */
export const MINUTE_MARK = "′";
/** `Tue 22 Sep`, in the tournament's own timezone. */
export function formatDocumentDate(iso, timezone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "short",
        day: "numeric",
        month: "numeric",
        year: "numeric",
    }).formatToParts(new Date(iso));
    const at = (type) => parts.find((part) => part.type === type)?.value ?? "";
    const monthIndex = Number.parseInt(at("month"), 10) - 1;
    // The weekday is taken from the formatter (it knows the zone) but normalised through our
    // own table, so "Tues" from some ICU build cannot reach a letter either.
    const weekday = at("weekday").slice(0, 3);
    const normalisedWeekday = WEEKDAYS_SHORT.find((day) => day === weekday) ?? weekday;
    return `${normalisedWeekday} ${at("day")} ${MONTHS_SHORT[monthIndex] ?? at("month")}`;
}
/** `Tue 22 Sep, 20:00`. §14's own spelling of a hearing time. */
export function formatDocumentDateTime(iso, timezone) {
    const time = new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(new Date(iso));
    return `${formatDocumentDate(iso, timezone)}, ${time}`;
}
/** `One` … `Ten` — §11's sanction sentence spells the number out. */
const NUMBER_WORDS = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
];
export function numberWord(n) {
    return NUMBER_WORDS[n] ?? String(n);
}
/** `A and B`, `A, B and C` — §10 joins person names with "and". */
export function joinNames(names) {
    if (names.length === 0)
        return "";
    if (names.length === 1)
        return names[0] ?? "";
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}
/**
 * §11's opener quotes the article text *"without trailing full stop"*, because the quotation
 * sits mid-sentence and the sample letters that kept the stop read as though the sentence
 * had ended twice.
 */
export function withoutTrailingFullStop(text) {
    return text.trimEnd().replace(/\.+$/, "");
}
/** `him / her`, resolved from the roster (§11's production note). */
function pronoun(person) {
    return person.gender === "f" ? "her" : "him";
}
/**
 * §10's person line: `PLAYER NO 7 KOR, PARK JAE-WON`, and for a non-player the ROLE in caps
 * with no shirt number — *"non-players use ROLE IN CAPS"*.
 */
function personHeadingLine(person, teamCode) {
    const team = teamCode ?? "";
    const name = person.name.toUpperCase();
    if (person.role === "Player" && person.shirtNumber !== null) {
        return `PLAYER NO ${String(person.shirtNumber)} ${team}, ${name}`.replace(/\s+,/, ",");
    }
    return `${person.role.toUpperCase()} ${team}, ${name}`.replace(/\s+,/, ",");
}
/**
 * §10c's persons clause: `player ABC #18`, `players A #7 and B #9`, `team official C`.
 *
 * Ruled by the Technical Delegate on 2026-09-10 after live testing. The incident statement
 * must identify the person(s) involved by **full name and shirt number**, because the live
 * letter read *"In the 3rd quarter at 33: Player throw a stick"* — a letter that charges
 * somebody without ever naming them inside the sentence that describes what they did.
 *
 * A person with no shirt number (a team official, a coach, or a player whose number was
 * never recorded) is written `<role in lower case> <name>` and **never** carries a bare `#`.
 * When every person involved is a player the noun is pluralised once at the front; a mixed
 * group gives each person their own descriptor, because "players X and coach Y" is wrong.
 */
function personsInvolvedClause(persons) {
    if (persons.length === 0)
        return "";
    const named = (person) => person.shirtNumber === null
        ? person.name
        : `${person.name} #${String(person.shirtNumber)}`;
    if (persons.every((person) => person.role === "Player")) {
        const noun = persons.length === 1 ? "player" : "players";
        return `${noun} ${joinNames(persons.map(named))}`;
    }
    return joinNames(persons.map((person) => `${person.role.toLowerCase()} ${named(person)}`));
}
/** The persons clause opening a sentence — `Player ABC #18 …` (§10c's no-match branch). */
function capitaliseFirst(text) {
    return text === "" ? "" : `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}
/**
 * §10c's incident sentence, composed ONCE for everything that shows it.
 *
 * The Hearing Letter calls this, and so does the Create New Hearing form's live preview
 * (§14, ruled by the Technical Delegate on 2026-09-10 after live testing). A second copy of
 * this composition living in the form would be a preview that can drift from the letter it
 * claims to preview, which is worse than no preview: the TD would be reading a sentence he
 * is not in fact about to create.
 *
 * Three branches, exactly as §10c fixes them:
 *
 * | Case | Sentence |
 * |---|---|
 * | A match, and a minute | `In the <phase>, at <minute>′, <persons clause> <details>` |
 * | A match, no minute | `In the <phase>, <persons clause> <details>` |
 * | No match (§14's off-pitch case) | `<Persons clause, capitalised> <details>` |
 *
 * **The details are never rewritten.** They are appended verbatim — no trimming, no
 * correction of grammar, tense or agreement. Editing a TD's account of an incident is not
 * this function's job, and the form's preview is a mirror, not an editor.
 */
export function incidentSentence(input) {
    const persons = personsInvolvedClause(input.personsInvolved);
    if (!input.hasMatch) {
        const openingPersonsPrefix = persons === "" ? "" : `${capitaliseFirst(persons)} `;
        return `${openingPersonsPrefix}${input.incidentDetails}`;
    }
    const personsPrefix = persons === "" ? "" : `${persons} `;
    // Ruled by the TD after live testing 2026-09-10: when a match minute is present the minute
    // mark (U+2032 PRIME) follows it, matching how the UI writes minutes. §10c then closes the
    // "when" with a comma in BOTH branches, so a case with a minute and a case without it read
    // the same way and the persons clause always follows the same joiner.
    const whenPrefix = input.matchMinute === null
        ? `In the ${input.matchPhase.toLowerCase()}, `
        : `In the ${input.matchPhase.toLowerCase()} at ${input.matchMinute}${MINUTE_MARK}, `;
    return `${whenPrefix}${personsPrefix}${input.incidentDetails}`;
}
/** §10 and §12 cite `Level N, Article X[ and Level N, Article Y]`. */
export function citeCharges(charges, joiner = " and ") {
    return charges
        .map((charge) => `Level ${String(charge.level)}, Article ${charge.article}`)
        .join(joiner);
}
/** §7: the case level is the maximum across the charges. */
function caseLevel(charges) {
    return charges.reduce((max, c) => (c.level > max ? c.level : max), 1);
}
/* ────────────────────────────────────────────────────────────────────────────
 * Refusals — a document that cannot be right is not produced
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Thrown when a document cannot be generated truthfully.
 *
 * It carries the Configuration section or the case field that is missing, because the TD's
 * next action is to go and set it, and "generation failed" without saying what is missing
 * is a dead end at a venue on a match day.
 */
export class HearingDocumentError extends Error {
    missing;
    statusCode = 422;
    code = "hearing_document_not_generatable";
    constructor(message, missing) {
        super(message);
        this.missing = missing;
    }
}
function demand(value, missing, message) {
    if (value === null || value === undefined || value === "") {
        throw new HearingDocumentError(message, missing);
    }
    return value;
}
/**
 * Every constant, checked once, before a word is composed.
 *
 * §2 lists these as Configuration items rather than code, and F5 exists because the mockup
 * carried a hard-coded TD email that differed from the configured one. A missing value is
 * therefore a refusal and never a placeholder: `<TD email>` printed on a letter that went
 * out is exactly the class of defect §1 says this module exists to end.
 */
export function assertDocumentConstants(constants) {
    demand(constants.eventName, "Configuration §01 Full Name For Letterheads", "The tournament has no full name for letterheads, so no document can carry its letterhead.");
    demand(constants.instrumentTitle, "Configuration §05 Bound Instruments", "No Code of Conduct edition is bound to this tournament, so a charge cannot be cited.");
    demand(constants.citationString, "Configuration §05 Citation String Used In Generated Documents", "No citation string is configured, so the Decision Letter cannot state the bound regulations.");
    demand(constants.venue, "Configuration §05 Default Hearing Venue", "No default hearing venue is configured.");
    const td = constants.technicalDelegate;
    demand(td.name, "Configuration §04 Technical Delegate — Signature Block", "The Technical Delegate signature block has no name.");
    demand(td.role, "Configuration §04 Technical Delegate — Signature Block", "The Technical Delegate signature block has no role.");
    demand(td.phone, "Configuration §04 Technical Delegate — Signature Block", "The Technical Delegate signature block has no telephone number.");
    demand(td.email, "Configuration §04 Technical Delegate — Signature Block", "The Technical Delegate signature block has no email address.");
}
/* ────────────────────────────────────────────────────────────────────────────
 * Batch 1 — the Hearing Letter (§10)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * §10's template, generated exactly as the specification writes it.
 *
 * **Two non-negotiable details, both pinned by tests.** The charge is put as a *possible*
 * breach and never as "has violated" — the presumption is preserved until the hearing, and
 * §10 says this "deliberately departs from the 2015–2017 samples' wording". And the
 * signature block comes from the constants, never from a literal.
 *
 * Six short paragraphs, per the brevity rule. Everything conditional in the template is
 * conditional here: the no-match variant, the video sentence, and the late-report sentence.
 */
export function generateHearingLetter(constants, hearingCase) {
    assertDocumentConstants(constants);
    const manager = demand(hearingCase.teamManagerName, "Team Manager", "The Hearing Letter is addressed to the Team Manager, and this case does not name one.");
    const hearingAt = demand(hearingCase.hearingAt, "Time Of The Hearing", "The Hearing Letter states the time and place of the hearing, and none is set.");
    if (hearingCase.charges.length === 0) {
        throw new HearingDocumentError("The Hearing Letter cites the article breached, and this case carries no charge.", "Charges");
    }
    const team = hearingCase.teamCode ?? "";
    const location = hearingCase.hearingLocation ?? constants.venue;
    const names = joinNames(hearingCase.personsInvolved.map((p) => p.name));
    const lines = [];
    lines.push(`To ${manager}`);
    lines.push(`Team Manager – ${team}`);
    lines.push("");
    lines.push("SCHEDULE OF DISCIPLINARY HEARING");
    for (const person of hearingCase.personsInvolved) {
        lines.push(personHeadingLine(person, hearingCase.teamCode));
    }
    lines.push("");
    // The salutation is the Team Manager's name EXACTLY as it is stored, in full — ruled by the
    // Technical Delegate on 2026-09-10 after live testing. The letter used to print the first
    // whitespace-separated word of the stored name, which produced "Dear Mr.," on a real letter
    // when the manager was recorded as "Mr. Wong". A TD on a match day often knows nothing but
    // "Mr. Wong", and honorifics, reversed CAPS surnames and single-token names all defeat any
    // first-name guess. So there is no honorific parsing and no first-name extraction here:
    // whatever the TD typed is what the Team Manager reads.
    lines.push(`Dear ${manager},`);
    lines.push("");
    lines.push(`As discussed, we will hold a disciplinary hearing on ` +
        `${formatDocumentDateTime(hearingAt, constants.timezone)} in the ${location}.`);
    lines.push("");
    // "possible breach" — non-negotiable detail 1.
    lines.push("The hearing is about a possible breach of the Code of Conduct");
    lines.push(`(${constants.instrumentTitle} – ${citeCharges(hearingCase.charges)}).`);
    lines.push("");
    // §10c, ruled by the TD after live testing 2026-09-10: the incident statement names every
    // person involved, in full, with the shirt number where one exists. The clause appears in
    // EVERY branch. The TD's free-text details are never rewritten — they follow verbatim.
    //
    // The sentence itself is `incidentSentence`, which the Create New Hearing form's live
    // preview also calls. One composition, so the preview cannot drift from the letter.
    const sentence = incidentSentence({
        hasMatch: hearingCase.match !== null,
        matchPhase: hearingCase.matchPhase,
        matchMinute: hearingCase.matchMinute,
        personsInvolved: hearingCase.personsInvolved,
        incidentDetails: hearingCase.incidentDetails,
    });
    const incidentParagraph = [];
    if (hearingCase.match === null) {
        incidentParagraph.push(`We have reviewed the report and the material provided. ${sentence}`);
    }
    else {
        const against = hearingCase.opponentTeam === null
            ? ""
            : ` (against ${hearingCase.opponentTeam} on ` +
                `${formatDocumentDate(hearingCase.match.date, constants.timezone)})`;
        incidentParagraph.push(`We have reviewed the footage from Match ${hearingCase.match.matchRef}${against} and ` +
            `identified the following. ${sentence}`);
    }
    if (hearingCase.hasVideoEvidence) {
        incidentParagraph.push("We will provide you with video of the incident from different angles, these will " +
            "also be available at the hearing.");
    }
    if (hearingCase.isLateReport) {
        // §8: the reasons are mandatory content on the generated hearing notice, and §5's foot
        // makes the discretion unappealable — so the letter must state why it was exercised.
        const reasons = hearingCase.lateReasons.join(" ");
        incidentParagraph.push(`The report reached us more than 24 hours after the incident. ${reasons}`.trimEnd());
    }
    lines.push(incidentParagraph.join(" "));
    lines.push("");
    lines.push(`${names} and yourself as Team Manager are required to attend. You are welcome to ` +
        "present any evidence or material you consider relevant to the incident or to the " +
        "penalty.");
    lines.push("");
    lines.push("Please acknowledge the receipt of this email.");
    lines.push("");
    lines.push("Kind regards,");
    lines.push("");
    lines.push(constants.technicalDelegate.name);
    lines.push(constants.technicalDelegate.role);
    lines.push(constants.technicalDelegate.phone);
    lines.push(constants.technicalDelegate.email);
    return lines.join("\n");
}
/* ────────────────────────────────────────────────────────────────────────────
 * The decision text — composed once, used by the minutes AND the decision letter
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * §11's decision builder, as one function.
 *
 * §11: *"Submitting clears any cached decision text so Batch 3 regenerates — this is what
 * stops the minutes and the letter drifting apart"*, and §12: *"the decision above is what
 * the Decision Letter reuses"*. Both sentences describe ONE requirement, and the way to keep
 * it is for both documents to call this, which they do.
 */
export function composeDecisionText(hearingCase, decision) {
    const names = joinNames(hearingCase.personsInvolved.map((p) => p.name));
    if (!decision.breachFound) {
        return (`No breach of the Code of Conduct is found. The report is dismissed and ${names} ` +
            "remains available for selection.");
    }
    const wordings = hearingCase.charges.map((c) => withoutTrailingFullStop(c.articleText));
    const blocks = [];
    const narrative = decision.severityNarrative?.trim() ?? "";
    blocks.push(`Breach of code of conduct (${wordings.join("; ")}). ${narrative}`.trimEnd());
    const levels = hearingCase.charges
        .map((c) => `Level ${String(c.level)} (Article ${c.article})`)
        .join(" and ");
    blocks.push(`According to the Code of Conduct levels: ${levels} and within the level: ` +
        `${decision.seriousness}.`);
    if (decision.sanction === "no_action") {
        blocks.push("No further action.");
    }
    else if (decision.sanction === "reprimand") {
        blocks.push(`Official reprimand to ${names}.`);
    }
    else {
        const matches = decision.matchesSuspended ?? 0;
        blocks.push(`${numberWord(matches)} match suspension.`);
    }
    return blocks.join("\n\n");
}
/* ────────────────────────────────────────────────────────────────────────────
 * Batch 2 — the Minutes (§11)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * §11's testimony auto-opener, one sentence per person.
 *
 * *"The first sentence is generated from the charges on this case. Everything after it is
 * yours."* — so this composes only the opener, and {@link generateMinutes} puts the
 * secretary's narrative after it.
 *
 * `him / her` resolves from the roster gender. §11's production note is explicit that using
 * both is *"exactly the defect found in the archived samples"*, so there is no branch here
 * that can emit the pair.
 */
export function composeTestimonyOpener(person, charges) {
    const shirt = person.shirtNumber === null ? "" : ` (No ${String(person.shirtNumber)})`;
    const clauses = charges.map((charge) => `under Article ${charge.article} of the Code of Conduct for ` +
        `"${withoutTrailingFullStop(charge.articleText)}"`);
    return (`The ${person.role} ${person.name}${shirt} was informed of the charge against ` +
        `${pronoun(person)} which was ${clauses.join("; and ")}.`);
}
/**
 * §11's `PRESENT:` block and §12's `ATTENDED BY:` block — **the recorded attendees when there
 * are any, and the automatic cast only as the empty-form fallback (§8, amended 2026-09-24).**
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT "THE AUTOMATIC ROWS FIRST, THEN THE RECORDED ATTENDEES"
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It used to be exactly that, and the TD reported the consequence after chairing two real
 * hearings on 24 September 2026: *"when I enter the 4 people in the attendance section, people
 * will appear twice… when I create the draft, it will always show 7-8 people by default"*. The
 * four people in the room are, in practice, the team manager (or the deputy who came instead),
 * the player, the minutes secretary and the chair — which is very nearly the automatic cast,
 * so concatenating the two lists prints most of the room twice and a served document then
 * misstates who attended a disciplinary hearing.
 *
 * The deeper point is that the two lists are not of the same kind. The automatic rows are an
 * *expectation* composed from the case record: the manager named on the case, the TD who owns
 * the tournament, the persons charged. The recorded attendees are the *record of who actually
 * sat in the room*, typed by the chair at the hearing. A hearing may be attended by an
 * Assistant Team Manager in the manager's place; a charged player may be absent and the
 * hearing proceed in absentia; the TD may have handed the chair over on a conflict. In every
 * one of those cases the automatic rows are not merely redundant, they are **wrong**, and the
 * minutes are the document an appeal reads (§9). Between an expectation and a record, the
 * record wins, and it wins entirely rather than being merged into the expectation — there is
 * no reliable way to match "Sun-Woo BAEK — Team Manager, KOR" against a typed row and know
 * whether it is the same human being, so de-duplication by name would be a guess printed as
 * a fact.
 *
 * The automatic rows survive for the one case where there is no record to prefer: a draft
 * composed before anybody has typed an attendee. There, printing an empty block under
 * `PRESENT:` would be worse than printing the expected cast, and the TD is looking at a draft
 * he is about to correct. So: **non-empty attendees are the whole list; empty attendees fall
 * back to manager · TD (Chair) · persons involved, exactly as before.**
 *
 * The web minutes form now seeds its attendee rows with that same automatic cast as ordinary
 * editable rows, so the TD's normal path is to correct a pre-fill rather than to type four
 * names underneath an unremovable list of three.
 */
function presentBlock(constants, hearingCase, minutes) {
    // The record of who attended, when there is one. It is the whole block.
    if (minutes.attendees.length > 0) {
        return minutes.attendees.map((attendee) => `${attendee.name} — ${attendee.role}`);
    }
    // Nothing recorded yet — the expected cast, so an early draft is not blank under the heading.
    const rows = [];
    if (hearingCase.teamManagerName !== null) {
        rows.push(`${hearingCase.teamManagerName} — Team Manager, ${hearingCase.teamCode ?? ""}`.trimEnd());
    }
    rows.push(`${constants.technicalDelegate.name} — ${constants.technicalDelegate.role} (Chair)`);
    for (const person of hearingCase.personsInvolved) {
        const shirt = person.shirtNumber === null ? "" : ` No ${String(person.shirtNumber)}`;
        rows.push(`${person.role}${shirt}, ${hearingCase.teamCode ?? ""} — person involved`.replace(", —", " —"));
    }
    return rows;
}
/**
 * §11's minutes document, headings in the specified order.
 *
 * `CODE OF CONDUCT HEARING — MINUTES` · `PRESENT:` · `INCIDENT:` · `TESTIMONY:` ·
 * `EVIDENCE PRODUCED:` · `DISCIPLINARY HISTORY:` · `ATTITUDE AT THE HEARING:` ·
 * `PENALTY ALREADY INCURRED:` · `DECISION:` · signature line.
 *
 * The minutes are the record kept for any appeal (§9), which is why the decision is composed
 * into them rather than merely referenced: an appeal reads this document.
 */
export function generateMinutes(constants, hearingCase, minutes, decision) {
    assertDocumentConstants(constants);
    const lines = [];
    lines.push(`${constants.eventName} · Code Of Conduct Hearing · Minutes`);
    lines.push("");
    lines.push("CODE OF CONDUCT HEARING — MINUTES");
    lines.push("");
    lines.push(`Case ${hearingCase.caseNumber}`);
    const when = [];
    if (hearingCase.hearingAt !== null) {
        when.push(formatDocumentDate(hearingCase.hearingAt, constants.timezone));
    }
    if (minutes.heldFrom !== null && minutes.heldTo !== null) {
        when.push(`${minutes.heldFrom}–${minutes.heldTo}`);
    }
    if (when.length > 0)
        lines.push(when.join(", "));
    lines.push(hearingCase.hearingLocation ?? constants.venue);
    if (minutes.matchSetting !== null)
        lines.push(minutes.matchSetting);
    lines.push("");
    lines.push("PRESENT:");
    lines.push(...presentBlock(constants, hearingCase, minutes));
    lines.push("");
    // §11: in absentia inserts this line into the minutes.
    if (hearingCase.inAbsentia) {
        lines.push("The person subject of the report did not attend; the hearing proceeded in absentia.");
        lines.push("");
    }
    lines.push("INCIDENT:");
    lines.push(hearingCase.incidentDetails);
    lines.push("");
    lines.push("TESTIMONY:");
    for (const person of hearingCase.personsInvolved) {
        lines.push(composeTestimonyOpener(person, hearingCase.charges));
    }
    if (minutes.testimony.trim() !== "") {
        lines.push(minutes.testimony.trim());
    }
    lines.push("");
    lines.push("EVIDENCE PRODUCED:");
    lines.push(minutes.evidenceProduced.trim() === ""
        ? "Written report of the reporting official."
        : minutes.evidenceProduced.trim());
    lines.push("");
    lines.push("DISCIPLINARY HISTORY:");
    lines.push(decision.disciplinaryHistory?.trim() ?? "None recorded at this tournament.");
    lines.push("");
    lines.push("ATTITUDE AT THE HEARING:");
    lines.push(decision.attitude);
    lines.push("");
    lines.push("PENALTY ALREADY INCURRED:");
    lines.push(decision.penaltyAlreadyIncurred?.trim() ?? "None.");
    lines.push("");
    lines.push("DECISION:");
    lines.push(composeDecisionText(hearingCase, decision));
    lines.push("");
    // §11's signature line, composed from the row marked Minutes Secretary.
    const secretary = minutes.attendees.find((a) => a.isMinutesSecretary);
    const date = hearingCase.hearingAt === null
        ? ""
        : formatDocumentDate(hearingCase.hearingAt, constants.timezone);
    lines.push(`${secretary?.name ?? "— mark a row as Minutes Secretary —"} / Minutes Secretary / ${date}`
        .trimEnd());
    return lines.join("\n");
}
/* ────────────────────────────────────────────────────────────────────────────
 * Batch 3 — the Decision Letter (§12)
 * ──────────────────────────────────────────────────────────────────────────── */
/** §12's operative suspension block — the named matches, the squad reduction, the ban. */
function operativeSuspensionBlock(constants, hearingCase, decision) {
    const matches = decision.suspendedMatches;
    if (matches.length === 0)
        return [];
    const names = joinNames(hearingCase.personsInvolved.map((p) => p.name));
    const team = hearingCase.teamCode ?? "";
    const described = matches.map((match) => `match #${match.matchRef} (${match.homeTeam} v ${match.awayTeam}), ` +
        `${formatDocumentDate(match.date, constants.timezone)}, kick-off ${match.kickoff}`);
    const single = matches.length === 1;
    const first = matches[0];
    return [
        `The suspension applies to ${joinNames(described)}.`,
        `In accordance with ${constants.regulations.squadReduction}, the Matchday Squad of ` +
            `${team} for ${single ? `match #${first?.matchRef ?? ""}` : "each of these matches"} ` +
            "will consist of only 17 players.",
        `In accordance with ${constants.regulations.facilityBan}, ${names} may not enter the ` +
            "field of play or the technical facility areas — team benches, coaches boxes and " +
            `video tower — until the completion of ` +
            `${single ? `match #${first?.matchRef ?? ""}` : "the last of the named matches"}.`,
    ];
}
/**
 * §12's Decision Letter — Format A, the structured FIH "Report Decision" template
 * (Alfred, 2026-07-24), with the headings verbatim.
 *
 * **Four generation rules, distilled from the reference letters and each pinned by a test:**
 *
 *  1. A `Number of matches suspended:` line **always** appears — `0` when a breach is found
 *     without suspension, `none` when no breach is found.
 *  2. A reprimand may carry the conditional-escalation sentence.
 *  3. A suspension names the exact match — number, teams, date and kick-off — and the
 *     generator appends the squad reduction and the facility ban.
 *  4. Level 2/3, or a suspension that cannot be served in-tournament, adds the referral
 *     paragraph with a recommended sanction.
 *
 * **One letter per case, cc'd to the Competition Office** (CC-Q18/CC-Q19, ruled 2026-08-11):
 * the Competition Office is a `cc:` line beneath the addressees and not a fourth document.
 */
export function generateDecisionLetter(constants, hearingCase, minutes, decision) {
    assertDocumentConstants(constants);
    if (hearingCase.charges.length === 0) {
        throw new HearingDocumentError("The Decision Letter states the charge, and this case carries no charge.", "Charges");
    }
    const names = joinNames(hearingCase.personsInvolved.map((p) => p.name));
    const team = hearingCase.teamCode ?? "";
    const manager = hearingCase.teamManagerName;
    const lines = [];
    lines.push(constants.eventName);
    lines.push("");
    lines.push("REPORT DECISION DISCIPLINARY HEARING");
    lines.push("");
    if (manager !== null)
        lines.push(`To ${manager}, Team Manager – ${team}`);
    lines.push(`To ${names}`);
    // CC-Q18: the same letter, cc'd — not a separate Competition Office output.
    lines.push("cc: Competition Office");
    lines.push("");
    lines.push("REGARDING:");
    for (const person of hearingCase.personsInvolved) {
        lines.push(personHeadingLine(person, hearingCase.teamCode));
    }
    lines.push("");
    lines.push("DATE AND TIME OF THE HEARING:");
    const when = [];
    if (hearingCase.hearingAt !== null) {
        when.push(formatDocumentDate(hearingCase.hearingAt, constants.timezone));
    }
    if (minutes.heldFrom !== null && minutes.heldTo !== null) {
        when.push(`${minutes.heldFrom}–${minutes.heldTo}`);
    }
    when.push(hearingCase.hearingLocation ?? constants.venue);
    lines.push(when.join(", "));
    lines.push("");
    lines.push("ATTENDED BY:");
    lines.push(...presentBlock(constants, hearingCase, minutes));
    if (hearingCase.inAbsentia) {
        lines.push("");
        lines.push("The person subject of the report did not attend. The hearing proceeded in absentia " +
            "and the failure to attend has been taken into account.");
    }
    lines.push("");
    lines.push("CHARGE:");
    lines.push(`Breach of the Code of Conduct — ${constants.citationString}.`);
    for (const charge of hearingCase.charges) {
        lines.push(`Level ${String(charge.level)}, ${charge.article}: "${withoutTrailingFullStop(charge.articleText)}"`);
    }
    const where = hearingCase.match === null
        ? "by the conduct described below."
        : `by the conduct described below, in match #${hearingCase.match.matchRef} ` +
            `(${hearingCase.match.homeTeam} v ${hearingCase.match.awayTeam})` +
            `${hearingCase.matchMinute === null ? "" : `, ${hearingCase.matchPhase.toLowerCase()} at ${hearingCase.matchMinute}${MINUTE_MARK}`}` +
            `, on ${formatDocumentDate(hearingCase.match.date, constants.timezone)}.`;
    lines.push(where);
    lines.push("");
    lines.push("EVIDENCE PRODUCED:");
    const evidence = minutes.evidenceProduced.trim() === ""
        ? ["Written report of the reporting official"]
        : minutes.evidenceProduced
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line !== "");
    evidence.forEach((item, index) => lines.push(`${String(index + 1)}. ${item}`));
    lines.push("");
    lines.push("THE DISCIPLINARY HISTORY OF THE TEAM MEMBER:");
    lines.push(decision.disciplinaryHistory?.trim() ?? "None recorded at this tournament.");
    lines.push("");
    lines.push("THE ATTITUDE OF THE TEAM MEMBER AT THE HEARING:");
    lines.push(decision.attitude);
    lines.push("");
    lines.push("ANY PENALTY ALREADY INCURRED:");
    lines.push(decision.penaltyAlreadyIncurred?.trim() ?? "None.");
    lines.push("");
    lines.push("DECISION:");
    lines.push(decision.acceptsBreach
        ? "The breach was accepted."
        : "The breach was not accepted and a finding was required.");
    lines.push(decision.finding.trim());
    lines.push("");
    lines.push(composeDecisionText(hearingCase, decision));
    lines.push("");
    // Rule 3: a suspension names the exact matches and appends the two regulation sentences.
    const operative = operativeSuspensionBlock(constants, hearingCase, decision);
    if (operative.length > 0) {
        lines.push(...operative);
        lines.push("");
    }
    // Rule 2: a reprimand may carry the conditional-escalation sentence.
    if (decision.conditionalEscalation) {
        lines.push(`Should ${names} receive a yellow card in the remainder of the tournament or commit ` +
            "any further misconduct, a suspension will be likely.");
        lines.push("");
    }
    // Rule 1: the line ALWAYS appears — `none` with no breach, `0` for a breach that carried
    // no suspension. It is the line an FIH reader looks for first, and its absence on a
    // reprimand letter is the omission the reference letters were most often criticised for.
    const suspendedCount = !decision.breachFound
        ? "none"
        : String(decision.sanction === "suspension" ? (decision.matchesSuspended ?? 0) : 0);
    lines.push(`Number of matches suspended: ${suspendedCount}`);
    lines.push("");
    // Rule 4: level 2/3, or a suspension that cannot be served in full.
    const level = caseLevel(hearingCase.charges);
    if (decision.breachFound && (decision.referUpward || level >= 2)) {
        lines.push("REFERRAL:");
        const balance = decision.suspensionCannotBeServedInFull
            ? `The suspension cannot be served in full at this tournament because ${team} has no ` +
                "further matches, so the balance is referred with the report. "
            : "";
        const recommended = decision.matchesSuspended ?? 0;
        lines.push("This matter is reported in writing to the FIH Director General for possible referral " +
            "of the behaviour constituting the offence to the FIH Disciplinary Commissioner, in " +
            `accordance with ${constants.regulations.referral}. ${balance}` +
            `I would recommend a suspension of ${String(recommended)} match(es) to ${names}.`);
        lines.push("");
    }
    lines.push("REASONING:");
    lines.push(decision.reasoning.trim());
    lines.push("");
    lines.push(constants.technicalDelegate.name);
    lines.push(constants.technicalDelegate.role);
    lines.push(constants.technicalDelegate.phone);
    lines.push(constants.technicalDelegate.email);
    lines.push("");
    // §12's service block. The `RECEIVED:` line with date and time is proof of service, and
    // the appeal clock runs from it — which is why it is printed as a blank to be signed
    // rather than pre-filled with a time nobody has yet witnessed.
    lines.push(`RECEIVED: ${constants.serviceCity}, .............................. (date and time)`);
    if (manager !== null) {
        lines.push(`${manager}, Team Manager .............................. (signature)`);
    }
    lines.push(`${names} .............................. (signature)`);
    lines.push("The appeal period runs from service of this decision.");
    return lines.join("\n");
}
//# sourceMappingURL=hearing-documents.js.map